import React, { useEffect, useMemo, useRef } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { setToken, setUserId } from "./store/slices/authSlice";
import { performLogout } from "./utils/logoutSession";
import MainLayout from "./components/layouts/MainLayout";
import AuthLayout from "./components/layouts/AuthLayout";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import "./App.css";
import {
  fetchUserDetails,
  userProfileSelector,
  userLoadingSelector,
} from "./store/slices/userSlice";
import { setLoaderState } from "./store/slices/loaderSlice";
import DashboardLayout from "./components/layouts/DashboardLayout";
import { useAppDispatch, useAppSelector } from "./hooks/redux";
import { authSelector, userIdSelector } from "./store/slices/authSlice";
import AdminRoute from "./components/admin/AdminRoute/AdminRoute";
import { fetchChatList } from "./store/slices/chatSlice";
import { initAblyForUser } from "./services/ablyChatClient";
import { fetchCategories } from "./store/slices/categorySlice";
import { useNotificationDeviceLifecycle } from "./hooks/useNotification";
import {
  fetchConditions,
  fetchReviewCategories,
} from "./store/slices/conditionSlice";
import { CHAT_INIT_SESSION_KEY, ERROR_MESSAGES } from "./utils/constants";
import { useChatAblySubscriptions } from "./hooks/useChatAblySubscriptions";
import PublicLayout from "./components/layouts/PublicLayout";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";
import SoftGateAuthRedirect from "./components/auth/SoftGateAuthRedirect";
import { savePendingDeepLink } from "./utils/deepLink";
import { logErrorDev, isDevelopmentEnv } from "./utils/formatters";
import { lazyAuthPage, lazyPage } from "./routing/lazyPage";
import { getUserIdFromAccessToken } from "./utils/helpers";

const authPageElement = lazyAuthPage(() => import("./pages/AuthPage/AuthPage"));
const adminSetPasswordElement = lazyAuthPage(
  () => import("./pages/AuthPage/AdminSetPasswordPage")
);
const ssoCallbackElement = lazyAuthPage(
  () => import("./pages/SSOCallback/SSOCallback")
);
const itemsListPageElement = lazyPage(
  () => import("./pages/ItemsListPage/ItemsListPage")
);
const itemsDetailPageElement = lazyPage(
  () => import("./pages/ItemsDetailPage/ItemsDetailPage")
);
const shareableItemsDetailPageElement = lazyPage(
  () => import("./pages/ItemsDetailPage/ItemsDetailPage"),
  { shareableLink: true }
);
const searchPageElement = lazyPage(() => import("./pages/SearchPage/SearchPage"));
const profilePageElement = lazyPage(() => import("./pages/ProfilePage/ProfilePage"));
const userFavouritesElement = lazyPage(
  () => import("./pages/UserFavourites/UserFavourites")
);
const notificationCenterElement = lazyPage(
  () => import("./pages/NotificationCenter/NotificationCenterPage")
);
const notificationDetailElement = lazyPage(
  () => import("./pages/NotificationCenter/NotificationDetailPage")
);
const chatPageElement = lazyPage(() => import("./pages/ChatPage/ChatPage"));
const adminDashboardElement = lazyPage(
  () => import("./pages/Admin/AdminDashboard/AdminDashboard")
);
const aboutPageElement = lazyPage(() => import("./pages/StaticPages/About"));
const privacyPageElement = lazyPage(() => import("./pages/StaticPages/Privacy"));
const termsPageElement = lazyPage(() => import("./pages/StaticPages/Terms"));

const authVerificationRoutes = [
  {
    path: "/confirm-mail",
    element: lazyPage(
      () => import("./pages/AuthVerification/AuthVerification"),
      { type: "confirm-mail" as const, key: "Confirm Email" }
    ),
  },
  {
    path: "/forgot-password",
    element: lazyPage(
      () => import("./pages/AuthVerification/AuthVerification"),
      { type: "forgot-password" as const, key: "Forgot Password" }
    ),
  },
  {
    path: "/reset-forgot-password",
    element: lazyPage(
      () => import("./pages/AuthVerification/AuthVerification"),
      { type: "reset-forgot-password" as const, key: "Reset Password" }
    ),
  },
  {
    path: "/change-password",
    element: lazyPage(
      () => import("./pages/AuthVerification/AuthVerification"),
      { type: "change-password" as const, key: "Change Password" }
    ),
  },
];

const AppRouter: React.FC = () => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector(authSelector) as { token?: string | null };
  const token = authState?.token;
  const userId = useAppSelector(userIdSelector);
  const userProfile = useAppSelector(userProfileSelector);
  const userLoading = useAppSelector(userLoadingSelector);
  const hasToken = Boolean(token);
  const localToken = localStorage.getItem("user-token");
  const localRefreshToken = localStorage.getItem("refresh-token");
  const localAccessToken = localStorage.getItem("user-access-token");
  const expiresIn = localStorage.getItem("expires-in");
  const hasFetchedUserDetails = useRef(false);
  const hasFetchedGuestCatalog = useRef(false);

  useNotificationDeviceLifecycle({ hasToken, userId });
  useChatAblySubscriptions(Boolean(hasToken && userId));

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!hasToken || !userId) {
      sessionStorage.removeItem(CHAT_INIT_SESSION_KEY);
      return;
    }
    if (sessionStorage.getItem(CHAT_INIT_SESSION_KEY) === userId) {
      return;
    }
    initAblyForUser(userId);
    sessionStorage.setItem(CHAT_INIT_SESSION_KEY, userId);
  }, [hasToken, userId]);

  useEffect(() => {
    if (!hasToken || !userId) {
      return;
    }
    dispatch(fetchChatList());
  }, [dispatch, hasToken, userId]);

  useEffect(() => {
    if (hasToken) {
      hasFetchedGuestCatalog.current = false;
      return;
    }
    if (hasFetchedGuestCatalog.current) {
      return;
    }
    hasFetchedGuestCatalog.current = true;
    dispatch(fetchCategories());
    dispatch(fetchConditions());
  }, [dispatch, hasToken]);

  const redirectToLogin = () => {
    if (isDevelopmentEnv()) {
      return;
    }
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    if (!currentPath.includes("/auth/")) {
      savePendingDeepLink(`${currentPath}${currentSearch}`);
      void performLogout(dispatch);
    }
  };

  useEffect(() => {
    dispatch(setLoaderState(true));
    if (localToken && !token) {
      try {
        if (!localAccessToken) {
          void performLogout(dispatch);
        } else {
          const userIdFromSub = getUserIdFromAccessToken(localAccessToken);
          dispatch(
            setToken({
              id_token: localToken,
              refresh_token: localRefreshToken || "",
              expires_in: expiresIn ? parseInt(expiresIn) : null,
              userId: userIdFromSub,
              access_token: localAccessToken,
            })
          );
        }
      } catch {
        void performLogout(dispatch);
      }
    } else if (hasToken && !userId) {
      try {
        if (!localAccessToken) {
          void performLogout(dispatch);
        } else {
          dispatch(setUserId(getUserIdFromAccessToken(localAccessToken)));
        }
      } catch {
        void performLogout(dispatch);
      }
    }
    dispatch(setLoaderState(false));
    // eslint-disable-next-line
  }, [dispatch, token, localToken, hasToken, userId, localAccessToken]);

  useEffect(() => {
    if (!hasToken || !userId) {
      hasFetchedUserDetails.current = false;
      return;
    }
    if (userProfile) {
      hasFetchedUserDetails.current = true;
      return;
    }
    if (userLoading) {
      return;
    }

    if (hasFetchedUserDetails.current) return;

    hasFetchedUserDetails.current = true;

    dispatch(fetchUserDetails({ userId })).then(
      (result) => {
        if (fetchUserDetails.rejected.match(result)) {
          logErrorDev(ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS, result.payload);
          redirectToLogin();
          return;
        }

        const isBlockedByAdmin = Boolean(result.payload?.user?.blockedByAdmin);
        if (isBlockedByAdmin) {
          return;
        }

        dispatch(fetchCategories());
        dispatch(fetchConditions());
        dispatch(fetchReviewCategories());
      }
    ).catch((error) => {
      logErrorDev(ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS, error);
      hasFetchedUserDetails.current = false;
      redirectToLogin();
    });
    // eslint-disable-next-line
  }, [dispatch, hasToken, userId, userProfile, userLoading]);

  const router = useMemo(() => {
    const publicRoutes = [
      {
        element: <PublicLayout />,
        children: [
          {
            path: "about-us",
            element: aboutPageElement,
          },
          {
            path: "privacy-policy",
            element: privacyPageElement,
          },
          {
            path: "terms-and-conditions",
            element: termsPageElement,
          },
          {
            path: "items/share/:itemId",
            element: shareableItemsDetailPageElement,
          },
        ],
      },
    ];

    const authRoutes = hasToken
      ? [
        {
          path: "/auth/sso/callback",
          element: ssoCallbackElement,
        },
        {
          path: "/auth/login",
          element: <Navigate to="/" replace />,
        },
        {
          path: "/auth/signup",
          element: <Navigate to="/" replace />,
        },
      ]
      : [
        {
          path: "/auth",
          element: <AuthLayout />,
          children: [
            { path: "login", element: authPageElement },
            { path: "signup", element: authPageElement },
            { path: "admin/set-password", element: adminSetPasswordElement },
            { path: "sso/callback", element: ssoCallbackElement },
          ],
        },
      ];

    const browseRoutes = [
      {
        path: "/",
        element: <DashboardLayout />,
        children: [
          { index: true, element: itemsListPageElement },
          { path: "items/:itemId", element: itemsDetailPageElement },
          { path: "search", element: searchPageElement },
          { path: "vendor/:vendorId", element: profilePageElement },
          ...(hasToken
            ? [
              { path: "profile", element: profilePageElement },
              { path: "notifications", element: notificationCenterElement },
              {
                path: "notifications/:notificationId",
                element: notificationDetailElement,
              },
              {
                path: "/favourites",
                element: userFavouritesElement,
              },
              {
                path: "/admin",
                element: (
                  <AdminRoute>
                    <Outlet />
                  </AdminRoute>
                ),
                children: [
                  { index: true, element: adminDashboardElement },
                  { path: "items/:reviewId", element: itemsDetailPageElement },
                ],
              },
              { path: "chat", element: chatPageElement },
              { path: "chat/:roomId", element: chatPageElement },
            ]
            : []),
          { path: "not-found", element: <NotFoundPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ];

    return createBrowserRouter([
      {
        path: "/",
        element: <MainLayout />,
        errorElement: <ErrorPage />,
        children: [
          ...publicRoutes,
          ...authRoutes,
          ...browseRoutes,
          ...authVerificationRoutes,
          ...(hasToken
            ? []
            : [
              { path: "chat", element: <SoftGateAuthRedirect /> },
              { path: "chat/:roomId", element: <SoftGateAuthRedirect /> },
              { path: "notifications", element: <SoftGateAuthRedirect /> },
              {
                path: "notifications/:notificationId",
                element: <SoftGateAuthRedirect />,
              },
              { path: "profile", element: <SoftGateAuthRedirect /> },
              { path: "favourites", element: <SoftGateAuthRedirect /> },
            ]),
          { path: "not-found", element: <NotFoundPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ]);
  }, [hasToken]);

  return <RouterProvider router={router} />;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppRouter />
    </Provider>
  );
};

export default App;
